import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtUSD } from "../lib/format";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  Users,
  Building2,
} from "lucide-react";

const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const fmtUSDT = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

function DeltaBadge({ value }) {
  if (value == null)
    return (
      <span className="inline-flex items-center text-[10.5px] text-zinc-400 font-mono">
        <Minus size={9} strokeWidth={2} className="mr-0.5" />—
      </span>
    );
  const pos = value >= 0;
  const tone = pos
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-rose-700 bg-rose-50 border-rose-200";
  const Icon = value === 0 ? Minus : pos ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center text-[10.5px] font-mono px-1 py-0.5 rounded border ${tone}`}
    >
      <Icon size={9} strokeWidth={2} className="mr-0.5" />
      {pos ? "+" : ""}
      {value}%
    </span>
  );
}

export default function NetProfitAnalytics() {
  const [trend, setTrend] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [tab, setTab] = useState("team"); // 'team' | 'company'

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/net-profits/by-team", { params: { months } }),
        api.get("/net-profits/company-monthly", { params: { months } }),
      ]);
      setTrend(r.data);
      setCompany(c.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [months]);

  const window = trend?.window || [];
  const teams = trend?.teams || [];
  const companyRows = company?.months || [];

  return (
    <Layout
      title="Net Profit Analytics"
      subtitle="Per-team trends + company-wide net vs expenses · MoM deltas"
      testId="net-profit-analytics-page"
      actions={
        <div className="flex gap-2">
          {[6, 12, 24].map((n) => (
            <button
              key={n}
              onClick={() => setMonths(n)}
              data-testid={`np-analytics-window-${n}`}
              className={`h-9 px-3 rounded-md border hairline text-[12.5px] font-mono ${
                months === n
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white hover:bg-zinc-50"
              }`}
            >
              {n}m
            </button>
          ))}
        </div>
      }
    >
      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5 border-b hairline">
        <TabBtn
          active={tab === "team"}
          onClick={() => setTab("team")}
          icon={Users}
          testId="np-analytics-tab-team"
        >
          By Team
        </TabBtn>
        <TabBtn
          active={tab === "company"}
          onClick={() => setTab("company")}
          icon={Building2}
          testId="np-analytics-tab-company"
        >
          Company
        </TabBtn>
      </div>

      {loading || !trend ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : !window.length ? (
        <div className="stat-card p-12 text-center text-zinc-500 text-[13px]">
          No salary sheets recorded yet.
        </div>
      ) : tab === "team" ? (
        <ByTeam window={window} teams={teams} />
      ) : (
        <Company company={company} rows={companyRows} />
      )}
    </Layout>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`-mb-px px-3 py-2 inline-flex items-center gap-1.5 text-[13px] border-b-2 transition-colors ${
        active
          ? "border-zinc-900 text-zinc-900 font-medium"
          : "border-transparent text-zinc-500 hover:text-zinc-800"
      }`}
    >
      <Icon size={13} strokeWidth={1.7} />
      {children}
    </button>
  );
}

function ByTeam({ window, teams }) {
  return (
    <>
      {/* Per-team table */}
      <div className="stat-card overflow-hidden">
        <div className="p-4 border-b hairline">
          <div className="font-display font-bold tracking-tight flex items-center gap-1.5">
            <BarChart3
              size={14}
              strokeWidth={1.7}
              className="text-indigo-600"
            />
            Per-team net (USDT) · MoM
          </div>
          <div className="text-[11.5px] text-zinc-500 mt-0.5">
            Each team's monthly contribution with MoM deltas — compare ups and
            downs across the window
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-2.5 px-3 sticky left-0 bg-zinc-50/50">
                  Team
                </th>
                {window.map((m) => (
                  <th
                    key={m}
                    className="py-2.5 px-3 text-right font-medium whitespace-nowrap"
                  >
                    {monthLabel(m)}
                  </th>
                ))}
                <th className="py-2.5 px-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {teams.length === 0 ? (
                <tr>
                  <td
                    colSpan={window.length + 2}
                    className="py-6 text-center text-zinc-500"
                  >
                    No team data.
                  </td>
                </tr>
              ) : (
                teams.map((tm) => (
                  <tr
                    key={tm.team_name}
                    className="row-hover"
                    data-testid={`np-analytics-team-${tm.team_name}`}
                  >
                    <td className="py-2.5 px-3 font-medium sticky left-0 bg-white">
                      {tm.team_name}
                    </td>
                    {tm.series.map((s) => (
                      <td
                        key={s.month}
                        className="py-2.5 px-3 font-mono text-right whitespace-nowrap"
                      >
                        <div
                          className={
                            s.net_usdt >= 0
                              ? "text-emerald-700"
                              : "text-rose-700"
                          }
                        >
                          {fmtUSDT(s.net_usdt)}
                        </div>
                        <div className="mt-0.5">
                          <DeltaBadge value={s.delta} />
                        </div>
                      </td>
                    ))}
                    <td className="py-2.5 px-3 font-mono text-right font-semibold text-emerald-700">
                      {fmtUSDT(tm.total_net_usdt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Company({ company, rows }) {
  return (
    <>
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat
          label="Total net (USDT)"
          value={fmtUSDT(company?.total_net_usdt)}
          accent={
            (company?.total_net_usdt || 0) >= 0 ? "emerald" : "rose"
          }
          sub="window total"
          testId="np-company-total-net"
        />
        <Stat
          label="Total expenses (USDT)"
          value={fmtUSDT(company?.total_expense_usdt)}
          accent="amber"
          sub="window total"
          testId="np-company-total-expense"
        />
        <Stat
          label="Net − Expenses"
          value={fmtUSDT(company?.total_diff_usdt)}
          accent={
            (company?.total_diff_usdt || 0) >= 0 ? "emerald" : "rose"
          }
          sub="cumulative diff"
          testId="np-company-total-diff"
        />
        <Stat
          label="Latest month"
          value={fmtUSDT(rows[rows.length - 1]?.net_usdt || 0)}
          accent={
            (rows[rows.length - 1]?.net_usdt || 0) >= 0 ? "emerald" : "rose"
          }
          sub={
            <DeltaBadge value={rows[rows.length - 1]?.delta ?? null} />
          }
          testId="np-company-latest"
        />
      </div>

      {/* Company-wide monthly table */}
      <div className="stat-card overflow-hidden">
        <div className="p-4 border-b hairline">
          <div className="font-display font-bold tracking-tight flex items-center gap-1.5">
            <Building2
              size={14}
              strokeWidth={1.7}
              className="text-indigo-600"
            />
            Company net vs expenses · MoM
          </div>
          <div className="text-[11.5px] text-zinc-500 mt-0.5">
            Net profit (USDT) and expenses (USDT) per month with the difference
            and MoM net delta
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-2.5 px-3">Month</th>
                <th className="py-2.5 px-3 text-right">Net (USDT)</th>
                <th className="py-2.5 px-3 text-right">Expenses (USDT)</th>
                <th className="py-2.5 px-3 text-right">Diff (Net − Exp)</th>
                <th className="py-2.5 px-3 text-right">vs Prev Net</th>
                <th className="py-2.5 px-3 text-right">Teams</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-zinc-500">
                    No data.
                  </td>
                </tr>
              ) : (
                [...rows].reverse().map((r) => (
                  <tr
                    key={r.month}
                    className="row-hover"
                    data-testid={`np-company-month-${r.month}`}
                  >
                    <td className="py-2.5 px-3 font-medium">
                      {monthLabel(r.month)}
                    </td>
                    <td
                      className={`py-2.5 px-3 font-mono text-right ${
                        r.net_usdt >= 0 ? "text-emerald-700" : "text-rose-700"
                      }`}
                    >
                      {fmtUSDT(r.net_usdt)}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-right text-amber-700">
                      {fmtUSDT(r.expense_usdt)}
                    </td>
                    <td
                      className={`py-2.5 px-3 font-mono text-right font-medium ${
                        r.diff_usdt >= 0
                          ? "text-emerald-700"
                          : "text-rose-700"
                      }`}
                    >
                      {fmtUSDT(r.diff_usdt)}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <DeltaBadge value={r.delta} />
                    </td>
                    <td className="py-2.5 px-3 font-mono text-right text-zinc-500">
                      {r.team_count}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-zinc-50/60">
                <td className="py-2.5 px-3 text-[10.5px] uppercase tracking-widest text-zinc-500">
                  Window total
                </td>
                <td
                  className={`py-2.5 px-3 font-mono text-right font-semibold ${
                    (company?.total_net_usdt || 0) >= 0
                      ? "text-emerald-700"
                      : "text-rose-700"
                  }`}
                >
                  {fmtUSDT(company?.total_net_usdt)}
                </td>
                <td className="py-2.5 px-3 font-mono text-right font-semibold text-amber-700">
                  {fmtUSDT(company?.total_expense_usdt)}
                </td>
                <td
                  className={`py-2.5 px-3 font-mono text-right font-semibold ${
                    (company?.total_diff_usdt || 0) >= 0
                      ? "text-emerald-700"
                      : "text-rose-700"
                  }`}
                >
                  {fmtUSDT(company?.total_diff_usdt)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, sub, accent, testId }) {
  const cls =
    {
      emerald: "text-emerald-700",
      rose: "text-rose-700",
      amber: "text-amber-700",
    }[accent || ""] || "text-zinc-900";
  return (
    <div className="stat-card p-4" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className={`font-mono text-[20px] font-medium mt-1 ${cls}`}>
        {value}
      </div>
      {sub && (
        <div className="text-[10.5px] text-zinc-500 mt-1 font-mono">
          {sub}
        </div>
      )}
    </div>
  );
}
